import { z } from "zod";

/**
 * Catalogue entities (SPEC §5.2): provider-neutral, read-only, cached.
 * Money is a decimal string to avoid float rounding, as providers send it.
 */
export const Money = z.object({
  net: z.string().regex(/^\d+(\.\d+)?$/),
  gross: z.string().regex(/^\d+(\.\d+)?$/),
});
export type Money = z.infer<typeof Money>;

export const Architecture = z.enum(["x86", "arm"]);
export type Architecture = z.infer<typeof Architecture>;

export const Deprecation = z
  .object({
    announcedAt: z.iso.datetime(),
    unavailableAfter: z.iso.datetime(),
  })
  .nullable();
export type Deprecation = z.infer<typeof Deprecation>;

export const Zone = z.object({ code: z.string() });
export type Zone = z.infer<typeof Zone>;

export const Region = z.object({
  code: z.string(),
  description: z.string(),
  city: z.string(),
  country: z.string().length(2),
  latitude: z.number(),
  longitude: z.number(),
  zone: z.string(),
});
export type Region = z.infer<typeof Region>;

export const ServerTypeFamily = z.enum(["cost_optimized", "regular_purpose", "general_purpose"]);
export type ServerTypeFamily = z.infer<typeof ServerTypeFamily>;

export const RegionPrice = z.object({
  region: z.string(),
  hourly: Money,
  monthly: Money,
  includedTrafficBytes: z.number().int().nonnegative(),
  perTbTraffic: Money,
});
export type RegionPrice = z.infer<typeof RegionPrice>;

export const ServerType = z.object({
  code: z.string(),
  description: z.string(),
  /** Null when the provider reports a family we do not know yet. */
  family: ServerTypeFamily.nullable(),
  architecture: Architecture,
  cores: z.number().int().positive(),
  cpuKind: z.enum(["shared", "dedicated"]),
  memoryGb: z.number().positive(),
  diskGb: z.number().int().positive(),
  storage: z.enum(["local", "network"]),
  deprecation: Deprecation,
  regions: z.array(
    z.object({
      region: z.string(),
      available: z.boolean(),
      recommended: z.boolean(),
      deprecation: Deprecation,
    }),
  ),
  prices: z.array(RegionPrice),
});
export type ServerType = z.infer<typeof ServerType>;

/** A load balancer size (Hetzner `lb11`, `lb21`, `lb31`): its limits and prices per region. */
export const LoadBalancerType = z.object({
  code: z.string(),
  description: z.string(),
  maxConnections: z.number().int().nonnegative(),
  maxServices: z.number().int().nonnegative(),
  maxTargets: z.number().int().nonnegative(),
  maxCertificates: z.number().int().nonnegative(),
  /** When the provider deprecated the type; null while it is current. */
  deprecatedAt: z.iso.datetime().nullable(),
  prices: z.array(RegionPrice),
});
export type LoadBalancerType = z.infer<typeof LoadBalancerType>;

export const ImageKind = z.enum(["system", "app", "snapshot", "backup"]);
export type ImageKind = z.infer<typeof ImageKind>;

export const Image = z.object({
  /** Stable name for system and app images; null for snapshots and backups. */
  code: z.string().nullable(),
  providerRef: z.string(),
  kind: ImageKind,
  description: z.string(),
  osFlavor: z.string(),
  osVersion: z.string().nullable(),
  architecture: Architecture,
  rapidDeploy: z.boolean(),
  diskGb: z.number().nonnegative(),
  /** When the provider deprecated the image; null while it is current. */
  deprecatedAt: z.iso.datetime().nullable(),
});
export type Image = z.infer<typeof Image>;

export const PriceList = z.object({
  currency: z.string().length(3),
  vatRate: z.string(),
  publicIpv4: z.array(z.object({ region: z.string(), hourly: Money, monthly: Money })),
  floatingIps: z.array(
    z.object({
      family: z.enum(["ipv4", "ipv6"]),
      region: z.string(),
      monthly: Money,
    }),
  ),
  volumePerGbMonth: Money,
  snapshotPerGbMonth: Money,
  /** Backups cost this percentage of the server price. */
  backupPercent: z.string(),
});
export type PriceList = z.infer<typeof PriceList>;
